import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { ScoreBadge, VerdictPill } from "../components/VerdictBadge";
import { Gavel, Clock } from "lucide-react";

export default function RooftopLiveAuctions() {
  const { rooftop_id } = useParams();
  const [rooftop, setRooftop] = useState(null);
  const [lanes, setLanes] = useState([]);

  useEffect(() => {
    api.get(`/rooftops/${rooftop_id}`).then((r) => setRooftop(r.data));
    api.get(`/live-auctions/${rooftop_id}`).then((r) => setLanes(r.data));
  }, [rooftop_id]);

  const groups = lanes.reduce((acc, v) => {
    const key = v.source_detail.split("·")[0].trim();
    (acc[key] = acc[key] || []).push(v);
    return acc;
  }, {});

  return (
    <Layout variant="rooftop" rooftopId={rooftop_id} rooftopName={rooftop?.name}>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <Gavel size={12} /> Auctions · {new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}
        </div>
        <h1 className="text-3xl font-bold text-navy mt-1">Live auctions</h1>
        <p className="text-sm text-slate-500 mt-1 font-medium">Pre-scored VINs across today's lanes · locked max bids sync to Simulcast.</p>
      </div>

      <div className="space-y-6" data-testid="live-auctions-groups">
        {Object.entries(groups).map(([lane, items]) => (
          <div key={lane} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-navy text-white px-5 py-3 flex items-center justify-between">
              <div className="font-bold">{lane}</div>
              <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">{items.length} VINs</div>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((v) => (
                <div key={v.vin} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <ScoreBadge score={v.total_score} verdict={v.verdict} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <VerdictPill verdict={v.verdict} />
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                        <Clock size={10} /> {v.runs_at ? new Date(v.runs_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "pending"}
                      </span>
                    </div>
                    <div className="font-semibold text-navy truncate">
                      {v.year} {v.make} {v.model} <span className="text-slate-600 font-normal">{v.trim}</span>
                    </div>
                    <div className="text-xs text-slate-500">{v.mileage.toLocaleString()} mi · {v.exterior_color} · {v.source_detail}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Max bid</div>
                    <div className="text-lg font-bold tabular text-navy">${v.target_price.toLocaleString()}</div>
                    <div className="text-[11px] tabular text-verdict-strongText font-bold">+${v.projected_gross.toLocaleString()} gross</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {Object.keys(groups).length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
            No lanes scheduled. First lane opens tomorrow morning.
          </div>
        )}
      </div>
    </Layout>
  );
}
